import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Users, Building2, FolderKanban, CheckSquare, Receipt,
  Search, ChevronRight, ArrowLeft, Copy, Check, ShieldAlert,
  Loader2, Bell, UserCheck, Activity, Shield, Trash2, Lock,
  ToggleLeft, ToggleRight, ClipboardList, RefreshCw, Ban, UserCog,
  Eye, EyeOff, Calendar,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtGBP = (n) => `£${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s || "—"; } };
const fmtDateTime = (s) => { try { return new Date(s).toLocaleString(); } catch { return s || "—"; } };

const STATUS_DOT = { active: "#00F5D4", inactive: "#374151", outstanding: "#F59E0B", overdue: "#FF007A" };

const ROLES = ["owner", "admin", "member", "viewer", "client", "marketing_admin", "superadmin"];
const PLANS = ["free", "pro", "business", "enterprise"];

// ── Reusable components ───────────────────────────────────────────────────────

const CopyToken = ({ token }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(token); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <button onClick={copy} className="ml-2 text-slate-500 hover:text-[#00F5D4] transition" title="Copy token">
      {copied ? <Check className="w-3.5 h-3.5 text-[#00F5D4]" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

const Stat = ({ icon: Icon, value, label, color }) => (
  <div className="text-center">
    <div className="text-lg font-mono font-bold" style={{ color: color || "#F9FAFB" }}>{value}</div>
    <div className="text-[9px] text-slate-600 uppercase tracking-[0.2em] flex items-center gap-1 justify-center">
      <Icon className="w-2.5 h-2.5" /> {label}
    </div>
  </div>
);

const Section = ({ title, icon: Icon, count, accent, children }) => (
  <div className="border border-[#1F2937] bg-[#0D1117] rounded-lg overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#1F2937] bg-[#030712]">
      <Icon className="w-4 h-4" style={{ color: accent || "#6B7280" }} />
      <span className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">{title}</span>
      {count !== undefined && <span className="ml-auto text-xs font-mono text-slate-600">{count}</span>}
    </div>
    {children}
  </div>
);

const Badge = ({ children, color = "#6B7280" }) => (
  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border" style={{ color, borderColor: `${color}40`, background: `${color}10` }}>
    {children}
  </span>
);

// ── User Edit Modal ───────────────────────────────────────────────────────────

const UserEditModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({ role: user.role || "member", plan: user.plan || "free", new_password: "", suspended: !!user.suspended });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { role: form.role, plan: form.plan, suspended: form.suspended };
      if (form.new_password) payload.new_password = form.new_password;
      const { data } = await api.patch(`/admin/users/${user.user_id}`, payload);
      toast.success("User updated");
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#0D1117] border border-[#1F2937] rounded-xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-[#00F5D4] uppercase tracking-widest mb-1">Edit User</div>
            <div className="font-bold text-white">{user.name || user.email}</div>
            <div className="text-xs text-slate-500 font-mono">{user.email}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition text-xl leading-none">×</button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full bg-[#030712] border border-[#374151] text-white text-sm rounded-lg px-3 py-2">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Plan</label>
            <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
              className="w-full bg-[#030712] border border-[#374151] text-white text-sm rounded-lg px-3 py-2">
              {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Reset Password (leave blank to keep)</label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                placeholder="New password…"
                className="w-full bg-[#030712] border border-[#374151] text-white text-sm rounded-lg px-3 py-2 pr-10" />
              <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between py-2 px-3 bg-[#111827] rounded-lg">
            <div>
              <div className="text-sm text-white">Account suspended</div>
              <div className="text-[10px] text-slate-500">Suspended users cannot log in</div>
            </div>
            <button onClick={() => setForm({ ...form, suspended: !form.suspended })}>
              {form.suspended
                ? <ToggleRight className="w-8 h-8 text-[#FF007A]" />
                : <ToggleLeft className="w-8 h-8 text-slate-500" />}
            </button>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button onClick={onClose} variant="ghost" className="flex-1 border border-[#374151] text-slate-400 hover:text-white">Cancel</Button>
          <Button onClick={save} disabled={busy} className="flex-1 bg-[#002FA7] hover:bg-[#002480] text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Users & Access tab ────────────────────────────────────────────────────────

const UsersTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const [tokens, setTokens] = useState({});
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/activity");
      setData(d);
    } catch (e) { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const impersonate = async (user) => {
    setImpersonating(user.user_id);
    try {
      const { data: d } = await api.post(`/admin/impersonate/${user.user_id}`);
      setTokens((prev) => ({ ...prev, [user.user_id]: d.token }));
      toast.success(`15-min token issued for ${d.target_user.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Impersonation failed"); }
    finally { setImpersonating(null); }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Permanently delete ${user.email}? This cannot be undone.`)) return;
    setDeleting(user.user_id);
    try {
      await api.delete(`/admin/users/${user.user_id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(null); }
  };

  const filtered = (data?.all_users || []).filter((u) =>
    !q || u.email?.toLowerCase().includes(q.toLowerCase()) || u.name?.toLowerCase().includes(q.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#00F5D4] animate-spin" /></div>;

  return (
    <div className="space-y-5">
      {editing && (
        <UserEditModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setData((prev) => ({
              ...prev,
              all_users: prev.all_users.map((u) => u.user_id === updated.user_id ? updated : u),
            }));
          }}
        />
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total users", value: data?.total_users, color: "#00F5D4" },
          { label: "Suspended", value: data?.suspended_users, color: data?.suspended_users > 0 ? "#FF007A" : "#6B7280" },
          { label: "Enterprise", value: data?.plan_breakdown?.enterprise || 0, color: "#002FA7" },
          { label: "Free tier", value: data?.plan_breakdown?.free || 0, color: "#6B7280" },
        ].map(({ label, value, color }) => (
          <div key={label} className="border border-[#1F2937] bg-[#0D1117] rounded-lg px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
            <div className="text-2xl font-mono font-bold" style={{ color }}>{value ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* Search + table */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
        <Input className="pl-9 bg-[#0D1117] border-[#1F2937] text-white placeholder:text-slate-600"
          placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <Section title={`All Users (${filtered.length})`} icon={Users}>
        <div className="divide-y divide-[#1F2937]">
          {filtered.length === 0 && <div className="px-4 py-6 text-xs text-slate-600 text-center">No users found.</div>}
          {filtered.map((u) => (
            <div key={u.user_id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`admin-user-${u.user_id}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold uppercase flex-shrink-0 ${u.suspended ? "bg-[#FF007A]/20 text-[#FF007A]" : "bg-[#1F2937] text-slate-300"}`}>
                {(u.name || u.email || "?")[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate flex items-center gap-2">
                  {u.name || u.email}
                  {u.suspended && <Badge color="#FF007A">SUSPENDED</Badge>}
                </div>
                <div className="text-[10px] text-slate-500 font-mono">{u.email} · joined {fmtDate(u.created_at)}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge color={u.role === "superadmin" ? "#FF007A" : u.role === "marketing_admin" ? "#7C3AED" : u.role === "owner" ? "#002FA7" : "#6B7280"}>{u.role || "member"}</Badge>
                <Badge color="#F59E0B">{u.plan || "free"}</Badge>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {tokens[u.user_id] ? (
                  <div className="flex items-center gap-1 font-mono text-[10px] text-[#00F5D4] bg-[#00F5D4]/10 border border-[#00F5D4]/20 px-2 py-1 rounded max-w-[120px]">
                    <span className="truncate">{tokens[u.user_id].slice(0, 16)}…</span>
                    <CopyToken token={tokens[u.user_id]} />
                  </div>
                ) : (
                  <button onClick={() => impersonate(u)} disabled={impersonating === u.user_id}
                    className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#00F5D4] border border-[#1F2937] hover:border-[#00F5D4]/30 px-2 py-1.5 rounded transition"
                    title="Impersonate (15-min token)">
                    {impersonating === u.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
                  </button>
                )}
                <button onClick={() => setEditing(u)}
                  className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-white border border-[#1F2937] hover:border-[#374151] px-2 py-1.5 rounded transition"
                  title="Edit user">
                  <UserCog className="w-3 h-3" />
                </button>
                {u.role !== "superadmin" && (
                  <button onClick={() => deleteUser(u)} disabled={deleting === u.user_id}
                    className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#FF007A] border border-[#1F2937] hover:border-[#FF007A]/30 px-2 py-1.5 rounded transition"
                    title="Delete user">
                    {deleting === u.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

// ── Activity tab ──────────────────────────────────────────────────────────────

const ActivityTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/admin/activity");
        setData(d);
      } catch { toast.error("Failed to load activity"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#00F5D4] animate-spin" /></div>;

  const planColors = { enterprise: "#002FA7", business: "#00F5D4", pro: "#F59E0B", free: "#6B7280" };

  return (
    <div className="space-y-5">
      {/* Plan breakdown */}
      <Section title="Plan distribution" icon={Activity}>
        <div className="px-4 py-4 flex flex-wrap gap-4">
          {Object.entries(data?.plan_breakdown || {}).map(([plan, count]) => (
            <div key={plan} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: planColors[plan] || "#374151" }} />
              <span className="text-sm text-white capitalize">{plan}</span>
              <span className="text-sm font-mono font-bold text-[#00F5D4]">{count}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Recent signups */}
      <Section title="Recent signups" icon={Users} count={data?.recent_signups?.length}>
        <div className="divide-y divide-[#1F2937]">
          {(data?.recent_signups || []).map((u) => (
            <div key={u.user_id} className="flex items-center gap-3 px-4 py-3">
              <div className="w-7 h-7 rounded-full bg-[#1F2937] flex items-center justify-center text-xs font-bold text-slate-300 uppercase flex-shrink-0">
                {(u.name || u.email || "?")[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{u.name || u.email}</div>
                <div className="text-[10px] text-slate-500 font-mono">{u.email}</div>
              </div>
              <Badge color={planColors[u.plan] || "#6B7280"}>{u.plan || "free"}</Badge>
              <span className="text-[10px] text-slate-600 font-mono flex-shrink-0">{fmtDate(u.created_at)}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Audit log */}
      <Section title="Admin audit log" icon={ClipboardList} count={data?.audit_logs?.length}>
        <div className="divide-y divide-[#1F2937]">
          {(data?.audit_logs || []).length === 0 && (
            <div className="px-4 py-6 text-xs text-slate-600 text-center">No audit events yet.</div>
          )}
          {(data?.audit_logs || []).map((log) => (
            <div key={log.log_id} className="flex items-start gap-3 px-4 py-3">
              <ClipboardList className="w-3.5 h-3.5 text-slate-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white capitalize">{log.action?.replace(/_/g, " ")}</div>
                <div className="text-[10px] text-slate-500 font-mono">
                  {log.target_email || log.target_user_id || log.tenant_id || ""}
                  {log.changes && ` · ${Object.keys(log.changes).join(", ")}`}
                </div>
              </div>
              <span className="text-[10px] text-slate-600 font-mono flex-shrink-0">{fmtDateTime(log.created_at)}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

// ── Demo bookings tab ─────────────────────────────────────────────────────────
// Fallback for marketing-site "Book a demo" requests, in case the sales-notification
// email bounces or gets buried — see backend/routes/demo_bookings.py.

const DEMO_STATUS_COLOR = { pending: "#F59E0B", contacted: "#00F5D4", closed: "#6B7280" };

const DemoBookingsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/demo-bookings");
      setBookings(data || []);
    } catch { toast.error("Failed to load demo bookings"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (bookingId, status) => {
    setUpdating(bookingId);
    try {
      await api.patch(`/admin/demo-bookings/${bookingId}`, { status });
      setBookings((prev) => prev.map((b) => (b.booking_id === bookingId ? { ...b, status } : b)));
    } catch { toast.error("Failed to update status"); }
    finally { setUpdating(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#00F5D4] animate-spin" /></div>;

  return (
    <Section title="Demo bookings" icon={Calendar} count={bookings.length}>
      <div className="divide-y divide-[#1F2937]">
        {bookings.length === 0 && (
          <div className="px-4 py-6 text-xs text-slate-600 text-center">No demo requests yet.</div>
        )}
        {bookings.map((b) => (
          <div key={b.booking_id} className="px-4 py-3 space-y-2">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white">{b.name} <span className="text-slate-500">· {b.company}</span></div>
                <div className="text-[10px] text-slate-500 font-mono">{b.email}</div>
              </div>
              <Badge color={DEMO_STATUS_COLOR[b.status] || "#6B7280"}>{b.status || "pending"}</Badge>
              <span className="text-[10px] text-slate-600 font-mono flex-shrink-0">{fmtDateTime(b.created_at)}</span>
            </div>
            <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
              {b.role && <span>Role: {b.role}</span>}
              {b.company_size && <span>Size: {b.company_size}</span>}
              {b.preferred_time && <span>Preferred: {b.preferred_time}</span>}
              {b.schedule_status && b.schedule_status !== "awaiting_slot" && (
                <span className={b.schedule_status === "canceled" ? "text-red-400" : "text-[#00F5D4]"}>
                  {b.schedule_status === "scheduled" ? "Scheduled" : "Canceled"}
                  {b.scheduled_at && `: ${fmtDateTime(b.scheduled_at)}`}
                </span>
              )}
              {b.meeting_url && (
                <a href={b.meeting_url} target="_blank" rel="noreferrer" className="text-[#00F5D4] hover:underline">
                  Join link
                </a>
              )}
            </div>
            {b.message && <div className="text-[11px] text-slate-400 italic">"{b.message}"</div>}
            <div className="flex gap-1.5 pt-1">
              {["pending", "contacted", "closed"].map((s) => (
                <button key={s} onClick={() => setStatus(b.booking_id, s)} disabled={updating === b.booking_id}
                  className={`text-[10px] font-mono px-2 py-1 rounded border transition disabled:opacity-50 ${
                    (b.status || "pending") === s
                      ? "border-[#00F5D4] text-[#00F5D4] bg-[#00F5D4]/10"
                      : "border-[#1F2937] text-slate-500 hover:text-white hover:border-slate-600"
                  }`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

// ── Policies tab ──────────────────────────────────────────────────────────────

const PoliciesTab = () => {
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/tenants");
        setTenants(data);
      } catch { toast.error("Failed to load tenants"); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadPolicy = async (tenantId) => {
    setSelected(tenantId);
    setPolicy(null);
    try {
      const { data } = await api.get(`/admin/tenants/${tenantId}/policy`);
      setPolicy(data);
    } catch { setPolicy({ tenant_id: tenantId }); }
  };

  const savePolicy = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/tenants/${selected}/policy`, {
        require_2fa: policy.require_2fa || false,
        max_seats: policy.max_seats || null,
        allowed_domains: policy.allowed_domains || [],
        disable_google_login: policy.disable_google_login || false,
      });
      setPolicy(data);
      toast.success("Policy saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#00F5D4] animate-spin" /></div>;

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-[280px_1fr] gap-5">
        {/* Tenant list */}
        <div className="border border-[#1F2937] rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-[#030712] border-b border-[#1F2937] text-[10px] uppercase tracking-widest text-slate-500 font-mono">Select tenant</div>
          <div className="divide-y divide-[#1F2937] max-h-[500px] overflow-y-auto">
            {tenants.map((t) => (
              <button key={t.tenant_id} onClick={() => loadPolicy(t.tenant_id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition hover:bg-[#111827] ${selected === t.tenant_id ? "bg-[#111827]" : ""}`}>
                <Building2 className="w-4 h-4 text-slate-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{t.tenant_name || t.tenant_id}</div>
                  <div className="text-[10px] text-slate-600 font-mono">{t.user_count} users</div>
                </div>
                {selected === t.tenant_id && <ChevronRight className="w-3.5 h-3.5 text-[#00F5D4]" />}
              </button>
            ))}
          </div>
        </div>

        {/* Policy editor */}
        {!selected && (
          <div className="flex items-center justify-center text-slate-600 text-sm border border-[#1F2937] rounded-lg">
            Select a tenant to manage its access policies
          </div>
        )}
        {selected && policy && (
          <div className="space-y-4">
            <div className="border border-[#1F2937] bg-[#0D1117] rounded-lg p-5 space-y-5">
              <div className="text-[10px] uppercase tracking-widest text-[#00F5D4] mb-2">Access Policies</div>

              {/* Policy toggles */}
              {[
                { key: "require_2fa", label: "Require 2FA", desc: "All users in this tenant must have two-factor authentication enabled" },
                { key: "disable_google_login", label: "Disable Google login", desc: "Users must log in with email/password only" },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between py-3 border-b border-[#1F2937]">
                  <div>
                    <div className="text-sm text-white">{label}</div>
                    <div className="text-[10px] text-slate-500">{desc}</div>
                  </div>
                  <button onClick={() => setPolicy({ ...policy, [key]: !policy[key] })}>
                    {policy[key]
                      ? <ToggleRight className="w-8 h-8 text-[#00F5D4]" />
                      : <ToggleLeft className="w-8 h-8 text-slate-500" />}
                  </button>
                </div>
              ))}

              {/* Max seats */}
              <div className="flex items-center justify-between py-3 border-b border-[#1F2937]">
                <div>
                  <div className="text-sm text-white">Max seats</div>
                  <div className="text-[10px] text-slate-500">Leave blank for unlimited</div>
                </div>
                <input type="number" min="1" value={policy.max_seats || ""}
                  onChange={(e) => setPolicy({ ...policy, max_seats: e.target.value ? Number(e.target.value) : null })}
                  placeholder="Unlimited"
                  className="w-28 bg-[#030712] border border-[#374151] text-white text-sm rounded-lg px-3 py-1.5 text-right" />
              </div>

              {/* Allowed domains */}
              <div className="py-3">
                <div className="text-sm text-white mb-1">Allowed email domains</div>
                <div className="text-[10px] text-slate-500 mb-2">Comma-separated. Leave blank to allow any domain.</div>
                <input
                  type="text"
                  value={(policy.allowed_domains || []).join(", ")}
                  onChange={(e) => setPolicy({ ...policy, allowed_domains: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="e.g. company.com, partner.org"
                  className="w-full bg-[#030712] border border-[#374151] text-white text-sm rounded-lg px-3 py-2" />
              </div>

              <Button onClick={savePolicy} disabled={saving} className="w-full bg-[#002FA7] hover:bg-[#002480] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                Save policies
              </Button>
            </div>

            {/* Current policy summary */}
            <div className="border border-[#1F2937] bg-[#0D1117] rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">Current policy</div>
              <pre className="text-xs text-[#00F5D4] font-mono whitespace-pre-wrap">
                {JSON.stringify(policy, null, 2)}
              </pre>
              {policy.updated_at && (
                <div className="text-[10px] text-slate-600 mt-2">Last updated {fmtDateTime(policy.updated_at)}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Tenants tab (existing) ────────────────────────────────────────────────────

const TenantList = ({ onSelect }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/tenants"); setTenants(data); }
      catch (e) { toast.error(e?.response?.data?.detail || "Failed to load tenants"); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = tenants.filter((t) =>
    !q || t.tenant_name?.toLowerCase().includes(q.toLowerCase()) || t.tenant_id.includes(q)
  );

  return (
    <div>
      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
        <Input className="pl-9 bg-[#0D1117] border-[#1F2937] text-white placeholder:text-slate-600"
          placeholder="Filter by name or tenant ID…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#00F5D4] animate-spin" /></div>
      ) : (
        <div className="border border-[#1F2937] divide-y divide-[#1F2937] rounded-lg overflow-hidden">
          {filtered.length === 0 && <div className="px-6 py-8 text-center text-sm text-slate-600">No tenants found.</div>}
          {filtered.map((t) => (
            <button key={t.tenant_id} onClick={() => onSelect(t.tenant_id)}
              className="w-full flex items-center gap-4 px-5 py-4 hover:bg-[#111827] transition text-left group">
              <div className="w-10 h-10 rounded-lg bg-[#002FA7]/20 border border-[#002FA7]/30 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-[#002FA7]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">{t.tenant_name || t.tenant_id}</div>
                <div className="text-[10px] text-slate-600 font-mono">{t.tenant_id}</div>
              </div>
              <div className="hidden md:flex items-center gap-6 text-right flex-shrink-0">
                <Stat icon={Users} value={t.user_count} label="users" />
                <Stat icon={FolderKanban} value={t.project_count} label="projects" />
                <Stat icon={CheckSquare} value={t.task_count} label="tasks" />
                <Stat icon={Receipt} value={fmt(t.invoice_count)} label="invoices" color={t.outstanding_amount > 0 ? "#F59E0B" : undefined} />
              </div>
              <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-white transition flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TenantDetail = ({ tenantId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data: d } = await api.get(`/admin/tenants/${tenantId}`); setData(d); }
      catch (e) { toast.error(e?.response?.data?.detail || "Failed to load tenant"); }
      finally { setLoading(false); }
    })();
  }, [tenantId]);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#00F5D4] animate-spin" /></div>;
  if (!data) return <div className="text-slate-500 text-sm py-8 text-center">Could not load tenant.</div>;

  const ts = data.task_summary || {};
  const totalTasks = Object.values(ts).reduce((s, v) => s + v, 0);

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-white transition">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to all tenants
      </button>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Users", value: data.users?.length, icon: Users },
          { label: "Projects", value: data.projects?.length, icon: FolderKanban },
          { label: "Tasks", value: totalTasks, icon: CheckSquare },
          { label: "Overdue invoices", value: data.overdue_invoices?.length, icon: Receipt, color: data.overdue_invoices?.length > 0 ? "#FF007A" : undefined },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="border border-[#1F2937] bg-[#0D1117] rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-mono">{label}</span>
            </div>
            <div className="text-2xl font-mono font-bold" style={{ color: color || "white" }}>{value ?? "—"}</div>
          </div>
        ))}
      </div>
      <Section title="Users" icon={Users} count={data.users?.length}>
        <div className="divide-y divide-[#1F2937]">
          {(data.users || []).map((u) => (
            <div key={u.user_id} className="flex items-center gap-3 px-4 py-3">
              <div className="w-8 h-8 rounded-full bg-[#1F2937] flex items-center justify-center text-xs font-bold text-slate-300 uppercase flex-shrink-0">
                {(u.name || u.email || "?")[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{u.name || u.email}</div>
                <div className="text-[10px] text-slate-500 font-mono">{u.email} · {u.role || "member"} · {u.plan || "free"}</div>
              </div>
              <Badge color="#F59E0B">{u.plan || "free"}</Badge>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Projects" icon={FolderKanban} count={data.projects?.length}>
        <div className="divide-y divide-[#1F2937]">
          {(data.projects || []).map((p) => (
            <div key={p.project_id} className="flex items-center gap-3 px-4 py-2.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: STATUS_DOT[p.health] || "#374151" }} />
              <span className="text-sm text-slate-300 flex-1 truncate">{p.name}</span>
              <span className="text-[10px] text-slate-600 font-mono capitalize">{p.status}</span>
            </div>
          ))}
          {!data.projects?.length && <div className="px-4 py-4 text-xs text-slate-600">No projects.</div>}
        </div>
      </Section>
    </div>
  );
};

// ── Main panel ────────────────────────────────────────────────────────────────

const AdminPanel = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("users");
  const [selectedTenant, setSelectedTenant] = useState(null);

  if (user?.role !== "superadmin") {
    return (
      <DarkPage><div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <ShieldAlert className="w-10 h-10 text-[#FF007A]" />
        <div className="text-lg font-bold text-white">Access denied</div>
        <div className="text-sm text-slate-500">This panel is restricted to superadmin accounts.</div>
      </div></DarkPage>
    );
  }

  const tabs = [
    { key: "users", label: "Users & Access", icon: Users },
    { key: "tenants", label: "Tenants", icon: Building2 },
    { key: "activity", label: "Activity", icon: Activity },
    { key: "demo-bookings", label: "Demo Bookings", icon: Calendar },
    { key: "policies", label: "Policies", icon: Shield },
  ];

  return (
    <DarkPage><div>
      <PageHeader
        eyebrow="Superadmin"
        title="Admin Control Panel"
        description="Manage users, control access, track activity, and set tenant policies."
      />
      <div className="px-5 sm:px-8 lg:px-12 py-6">
        <div className="flex gap-1 mb-6 border-b border-[#1F2937] overflow-x-auto">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => { setTab(key); setSelectedTenant(null); }}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-mono border-b-2 transition -mb-px whitespace-nowrap ${
                tab === key ? "border-[#00F5D4] text-[#00F5D4]" : "border-transparent text-slate-500 hover:text-white"
              }`}
              data-testid={`admin-tab-${key}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "users" && <UsersTab />}
        {tab === "tenants" && !selectedTenant && <TenantList onSelect={setSelectedTenant} />}
        {tab === "tenants" && selectedTenant && <TenantDetail tenantId={selectedTenant} onBack={() => setSelectedTenant(null)} />}
        {tab === "activity" && <ActivityTab />}
        {tab === "demo-bookings" && <DemoBookingsTab />}
        {tab === "policies" && <PoliciesTab />}
      </div>
    </div></DarkPage>
  );
};

export default AdminPanel;
